import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getVorlageCategory, getVorlageDisplayName, getVorlageSubsection } from "../../utils/vorlagenHelpers";
import { ActionMenu, AppButton, IconButton, TextPanel, UiIcon } from "../ui/Primitives";
import RichTextEditor from "../ui/RichTextEditor";
import { copyRichTextToClipboard, hasRichTextContent, richTextToPlainText } from "../../utils/richTextHelpers";

const DEFAULT_CATEGORY = "Allgemein";
const SUBSECTION_ORDER_BY_MOD = {
  MRT: ["Herz", "Kopf", "Hals", "Thorax", "Abdomen", "Becken", "Wirbelsäule", "Extremitäten"],
  "MRT 1,5": ["Herz", "Kopf", "Hals", "Thorax", "Abdomen", "Becken", "Wirbelsäule", "Extremitäten"],
  "MRT 3": ["Herz", "Kopf", "Hals", "Thorax", "Abdomen", "Becken", "Wirbelsäule", "Extremitäten"],
  CT: ["Herz", "Kopf", "Hals", "Thorax", "Abdomen", "Becken", "Wirbelsäule", "Extremitäten"],
};

const normalizeSubsection = (value) => String(value || "")
  .toLowerCase()
  .normalize("NFD")
  .replace(/[\u0300-\u036f]/g, "")
  .trim();

const normalizeCategory = (value) => String(value || "").trim() || DEFAULT_CATEGORY;

const createVorlageCategoryKey = (vorlage) => {
  const mod = String(vorlage?.mod || "").trim();
  const name = String(vorlage?.name || "").trim();
  if (!mod || !name) return "";
  return `stable:${mod}:${name}`;
};

const createVorlageAssignmentCategoryKey = (vorlage) => {
  const mod = String(vorlage?.mod || "").trim();
  const subsection = getVorlageSubsection(vorlage) || getVorlageDisplayName(vorlage);
  const displayName = getVorlageDisplayName(vorlage);
  if (!mod || !subsection || !displayName) return "";
  return `assignment:${mod}:${subsection}:${displayName}`;
};

const getVorlageCategoryValue = (categoryMap, vorlage) => (
  vorlage?.category ||
  getVorlageCategory(vorlage) ||
  categoryMap?.[vorlage?.id] ||
  categoryMap?.[createVorlageCategoryKey(vorlage)] ||
  categoryMap?.[createVorlageAssignmentCategoryKey(vorlage)] ||
  ""
);

const getSubsectionRank = (modGroup, subsectionName) => {
  const order = SUBSECTION_ORDER_BY_MOD[modGroup] || [];
  const normalizedName = normalizeSubsection(subsectionName);
  const index = order.findIndex((entry) => normalizeSubsection(entry) === normalizedName);
  return index === -1 ? Number.MAX_SAFE_INTEGER : index;
};

const compareSubsections = (modGroup, left, right) => {
  const leftRank = getSubsectionRank(modGroup, left);
  const rightRank = getSubsectionRank(modGroup, right);
  if (leftRank !== rightRank) return leftRank - rightRank;
  return left.localeCompare(right, "de", { numeric: true, sensitivity: "base" });
};

function VorlagenTab({
  c,
  dark,
  lbl,
  inp,
  vorlagenFilterText,
  setVorlagenFilterText,
  newPromptMod,
  setNewPromptMod,
  vorlagenGroupOptions,
  vorlagenCreateGroupOptions,
  newVorlagenGroupName,
  setNewVorlagenGroupName,
  onCreateVorlagenGroup,
  onMoveVorlagenGroup,
  onDeleteVorlagenGroup,
  newPromptName,
  setNewPromptName,
  newPromptSubsection,
  setNewPromptSubsection,
  newPromptCategory,
  setNewPromptCategory,
  newPromptBefund,
  setNewPromptBefund,
  newPromptBeurteilung,
  setNewPromptBeurteilung,
  onSaveNewPrompt,
  isSavingNewPrompt,
  vorlagenGruppiert,
  expandedVorlagenGroups,
  toggleVorlagenGroup,
  filterVorlagen,
  loadVorlageToEditor,
  onSavePreviewPrompt,
  onDeletePrompt,
  onRenameSubsection,
  onDeleteSubsection,
  subsectionOrderByMod,
  onMoveSubsection,
  onRenameSubsectionOrder,
  onDeleteSubsectionOrder,
  categoryById,
  onChangeCategory,
  emptyCategoriesBySubsection,
  onAddEmptyCategory,
  modBadge,
  modColor,
  showStatus,
  newPromptFormOpen,
  setNewPromptFormOpen,
  newPromptMrtTarget,
  setNewPromptMrtTarget,
  Btn,
}) {
  const [previewVorlage, setPreviewVorlage] = useState(null);
  const [selectedPreviewText, setSelectedPreviewText] = useState("");
  const [selectedSource, setSelectedSource] = useState("");
  const [previewDraft, setPreviewDraft] = useState(null);
  const [isSavingPreview, setIsSavingPreview] = useState(false);
  const [expandedSubsections, setExpandedSubsections] = useState({});
  const [newSubsectionDraftByMod, setNewSubsectionDraftByMod] = useState({});
  const [pendingSubsectionsByMod, setPendingSubsectionsByMod] = useState({});
  const [editingSubsection, setEditingSubsection] = useState({ key: "", value: "" });
  const [newCategoryDraftBySubsection, setNewCategoryDraftBySubsection] = useState({});
  const [activeCategoryCreateKey, setActiveCategoryCreateKey] = useState("");
  const [isCreateSectionModalOpen, setIsCreateSectionModalOpen] = useState(false);
  const befundPreviewRef = useRef(null);
  const beurteilungPreviewRef = useRef(null);
  const creationGroupOptions = useMemo(() => vorlagenCreateGroupOptions || [], [vorlagenCreateGroupOptions]);
  const categoryOptions = useMemo(() => {
    const options = new Set();
    Object.values(categoryById || {}).forEach((value) => {
      const category = normalizeCategory(value);
      if (category !== DEFAULT_CATEGORY) options.add(category);
    });
    return Array.from(options).sort((a, b) => a.localeCompare(b, "de", { numeric: true, sensitivity: "base" }));
  }, [categoryById]);

  const canCreateSubsection = (modGroup) => !!modGroup;

  const getGroupLabel = (modGroup) => {
    if (modGroup === "CT") return "Computertomographie";
    if (modGroup === "MRT") return "Magnetresonanztomographie (gemeinsame Vorlagen)";
    if (modGroup === "Modalitäten") return "Modalitäten";
    if (modGroup === "MRT 1,5" || modGroup === "MRT 3") {
      return `Magnetresonanztomographie ${modGroup.replace("MRT ", "")}`;
    }

    return modGroup;
  };

  const getSubsectionCategoryKey = (modGroup, subsectionName) => `subsection:${modGroup}:${subsectionName}`;

  const getSubsectionCategoryOptions = (list, subsectionKey) => {
    const values = new Set();
    (list || []).forEach((vorlage) => values.add(normalizeCategory(getVorlageCategoryValue(categoryById, vorlage))));
    (emptyCategoriesBySubsection?.[subsectionKey] || []).forEach((category) => values.add(normalizeCategory(category)));
    return Array.from(values).sort((a, b) => {
      if (a === DEFAULT_CATEGORY) return -1;
      if (b === DEFAULT_CATEGORY) return 1;
      return a.localeCompare(b, "de", { numeric: true, sensitivity: "base" });
    });
  };

  const addCategoryToSubsection = (modGroup, subsectionName) => {
    const subsectionKey = getSubsectionCategoryKey(modGroup, subsectionName);
    const draftKey = `${subsectionKey}:draft`;
    const category = normalizeCategory(newCategoryDraftBySubsection[draftKey]);
    if (category === DEFAULT_CATEGORY) {
      showStatus("Bitte Kategorienamen eingeben.", true);
      return;
    }

    onAddEmptyCategory?.(subsectionKey, category);
    setNewCategoryDraftBySubsection((prev) => ({ ...prev, [draftKey]: "" }));
    setActiveCategoryCreateKey("");
    setExpandedSubsections((prev) => ({ ...prev, [`${subsectionKey}:category:${category}`]: true }));
  };

  const changeVorlageCategory = async (vorlageOrId, nextCategory) => {
    const vorlage = typeof vorlageOrId === "object" ? vorlageOrId : null;
    const vorlageId = vorlage?.id || vorlageOrId;
    const normalizedCategory = normalizeCategory(nextCategory) === DEFAULT_CATEGORY ? "" : nextCategory;
    if (vorlage && onSavePreviewPrompt) {
      const ok = await onSavePreviewPrompt({
        ...vorlage,
        name: getVorlageDisplayName(vorlage),
        subsection: getVorlageSubsection(vorlage),
        category: normalizedCategory,
      });
      if (!ok) return;
    }
    onChangeCategory?.(vorlageOrId, normalizedCategory);
    setPreviewDraft((prev) => (prev?.id === vorlageId ? { ...prev, category: normalizedCategory } : prev));
    setPreviewVorlage((prev) => (prev?.id === vorlageId ? { ...prev, category: normalizedCategory } : prev));
  };

  const applySubsectionToForm = (modGroup, subsectionName) => {
    const normalizedName = String(subsectionName || "").trim();
    if (!normalizedName) {
      showStatus("Bitte Untersektion eingeben.", true);
      return;
    }

    if (!canCreateSubsection(modGroup)) {
      showStatus("Für diese Gruppe ist Untersektion-Erstellung derzeit nicht verfügbar.", true);
      return;
    }

    if (modGroup === "MRT" || modGroup === "MRT 1,5" || modGroup === "MRT 3") {
      setNewPromptMod("MRT");
      setNewPromptMrtTarget(modGroup === "MRT" ? "MRT" : modGroup);
    } else {
      setNewPromptMod(modGroup);
      setNewPromptMrtTarget("MRT");
    }

    setPendingSubsectionsByMod((prev) => {
      const current = prev[modGroup] || [];
      if (current.some((entry) => normalizeSubsection(entry) === normalizeSubsection(normalizedName))) {
        return prev;
      }

      return {
        ...prev,
        [modGroup]: [...current, normalizedName],
      };
    });

    setNewPromptSubsection(normalizedName);
    setNewPromptName("");
    setNewPromptFormOpen(true);
    showStatus(`Untersektion "${normalizedName}" vorbereitet ✓`);
  };

  const toggleSubsection = (key) => {
    setExpandedSubsections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const createNewSubsection = (modGroup) => {
    const draft = String(newSubsectionDraftByMod[modGroup] || "").trim();
    if (!draft) {
      showStatus("Bitte Untersektion eingeben.", true);
      return;
    }

    applySubsectionToForm(modGroup, draft);
    setNewSubsectionDraftByMod((prev) => ({ ...prev, [modGroup]: "" }));
  };

  const renamePendingSubsection = (modGroup, previousName, nextName) => {
    setPendingSubsectionsByMod((prev) => {
      const current = prev[modGroup] || [];
      const renamed = current.map((entry) => (
        normalizeSubsection(entry) === normalizeSubsection(previousName) ? nextName : entry
      ));
      const unique = renamed.filter((entry, index) => (
        renamed.findIndex((candidate) => normalizeSubsection(candidate) === normalizeSubsection(entry)) === index
      ));
      return { ...prev, [modGroup]: unique };
    });
  };

  const removePendingSubsection = (modGroup, subsectionName) => {
    setPendingSubsectionsByMod((prev) => ({
      ...prev,
      [modGroup]: (prev[modGroup] || []).filter((entry) => normalizeSubsection(entry) !== normalizeSubsection(subsectionName)),
    }));
  };

  const beginRenameSubsection = (subsectionKey, subsectionName) => {
    setEditingSubsection({ key: subsectionKey, value: subsectionName });
  };

  const cancelRenameSubsection = () => {
    setEditingSubsection({ key: "", value: "" });
  };

  const handleRenameSubsection = async (modGroup, subsectionName, list) => {
    const nextName = String(editingSubsection.value || "").trim();
    if (!nextName) {
      showStatus("Bitte Untersektion eingeben.", true);
      return;
    }

    if (normalizeSubsection(nextName) === normalizeSubsection(subsectionName)) {
      cancelRenameSubsection();
      return;
    }

    if (list.length === 0) {
      renamePendingSubsection(modGroup, subsectionName, nextName);
      onRenameSubsectionOrder?.(modGroup, subsectionName, nextName);
      if (normalizeSubsection(newPromptSubsection) === normalizeSubsection(subsectionName)) {
        setNewPromptSubsection(nextName);
      }
      showStatus("Untersektion aktualisiert ✓");
      cancelRenameSubsection();
      return;
    }

    const ok = await onRenameSubsection?.(list, nextName);
    if (!ok) return;
    renamePendingSubsection(modGroup, subsectionName, nextName);
    onRenameSubsectionOrder?.(modGroup, subsectionName, nextName);
    if (normalizeSubsection(newPromptSubsection) === normalizeSubsection(subsectionName)) {
      setNewPromptSubsection(nextName);
    }
    cancelRenameSubsection();
  };

  const handleDeleteSubsection = async (modGroup, subsectionName, list) => {
    const templateCount = list.length;
    const confirmed = window.confirm(
      templateCount > 0
        ? `Untersektion "${subsectionName}" mit ${templateCount} Vorlage${templateCount === 1 ? "" : "n"} löschen?`
        : `Leere Untersektion "${subsectionName}" löschen?`
    );
    if (!confirmed) return;

    const ok = await onDeleteSubsection?.(list);
    if (!ok) return;
    removePendingSubsection(modGroup, subsectionName);
    onDeleteSubsectionOrder?.(modGroup, subsectionName);
    if (normalizeSubsection(newPromptSubsection) === normalizeSubsection(subsectionName)) {
      setNewPromptSubsection("");
    }
    if (editingSubsection.key) {
      cancelRenameSubsection();
    }
  };

  const isSearching = !!String(vorlagenFilterText || "").trim();
  const groupedVorlagenByMod = useMemo(() => {
    return (vorlagenGroupOptions || []).reduce((acc, modGroup) => {
      const grouped = {};
      const vorlagen = filterVorlagen(vorlagenGruppiert[modGroup] || []);

      vorlagen.forEach((vorlage) => {
        const subsection = getVorlageSubsection(vorlage) || getVorlageDisplayName(vorlage) || "Ohne Untersektion";
        if (!grouped[subsection]) grouped[subsection] = [];
        grouped[subsection].push(vorlage);
      });

      if (!isSearching) {
        (pendingSubsectionsByMod[modGroup] || []).forEach((subsection) => {
          if (!subsection) return;
          if (!grouped[subsection]) grouped[subsection] = [];
        });
      }

      const preferredOrder = subsectionOrderByMod?.[modGroup] || [];
      const preferredIndex = new Map(preferredOrder.map((entry, index) => [normalizeSubsection(entry), index]));

      acc[modGroup] = Object.entries(grouped).sort((a, b) => {
        const leftOrder = preferredIndex.get(normalizeSubsection(a[0]));
        const rightOrder = preferredIndex.get(normalizeSubsection(b[0]));
        const leftHasOrder = leftOrder != null;
        const rightHasOrder = rightOrder != null;

        if (leftHasOrder && rightHasOrder) return leftOrder - rightOrder;
        if (leftHasOrder) return -1;
        if (rightHasOrder) return 1;

        return compareSubsections(modGroup, a[0], b[0]);
      });
      return acc;
    }, {});
  }, [filterVorlagen, isSearching, pendingSubsectionsByMod, subsectionOrderByMod, vorlagenGroupOptions, vorlagenGruppiert]);

  const closePreview = useCallback(() => {
    setPreviewVorlage(null);
    setSelectedPreviewText("");
    setSelectedSource("");
    setPreviewDraft(null);
    setIsSavingPreview(false);
  }, []);

  const hasUnsavedPreviewChanges = useCallback(() => {
    if (!previewDraft || !previewVorlage) return false;
    return (
      String(previewDraft.name || "") !== String(getVorlageDisplayName(previewVorlage) || "") ||
      String(previewDraft.subsection || "") !== String(getVorlageSubsection(previewVorlage) || "") ||
      String(previewDraft.category || "") !== String(getVorlageCategoryValue(categoryById, previewVorlage) || "") ||
      String(previewDraft.befund || "") !== String(previewVorlage.befund || "") ||
      String(previewDraft.beurteilung || "") !== String(previewVorlage.beurteilung || "")
    );
  }, [categoryById, previewDraft, previewVorlage]);

  const confirmDiscardPreviewChanges = useCallback(() => (
    !hasUnsavedPreviewChanges() || window.confirm("Ungespeicherte Änderungen verwerfen?")
  ), [hasUnsavedPreviewChanges]);

  const requestClosePreview = useCallback(() => {
    if (!confirmDiscardPreviewChanges()) return;
    closePreview();
  }, [closePreview, confirmDiscardPreviewChanges]);

  const openPreview = (vorlage) => {
    if (previewVorlage?.id === vorlage?.id) return;
    if (!confirmDiscardPreviewChanges()) return;
    setPreviewVorlage(vorlage);
    setSelectedPreviewText("");
    setSelectedSource("");
    setPreviewDraft({
      ...vorlage,
      name: getVorlageDisplayName(vorlage),
      subsection: getVorlageSubsection(vorlage),
      category: getVorlageCategoryValue(categoryById, vorlage) || "",
      befund: vorlage.befund || "",
      beurteilung: vorlage.beurteilung || "",
    });
  };

  const saveInlineEdit = async () => {
    if (!previewDraft || !onSavePreviewPrompt) return;
    showStatus("Speichern...");
    setIsSavingPreview(true);
    const ok = await onSavePreviewPrompt(previewDraft);
    setIsSavingPreview(false);
    if (!ok) return;
    onChangeCategory?.(previewDraft, normalizeCategory(previewDraft.category) === DEFAULT_CATEGORY ? "" : previewDraft.category);
    setPreviewVorlage(previewDraft);
    setPreviewDraft((prev) => ({ ...prev }));
  };

  const currentPreview = previewDraft || previewVorlage;
  const previewIsDirty = hasUnsavedPreviewChanges();
  const workspaceStyle = {
    display: "grid",
    gridTemplateColumns: previewVorlage ? "minmax(340px, 0.95fr) minmax(520px, 1.35fr)" : "minmax(0, 1fr)",
    gap: 16,
    alignItems: "start",
  };
  const splitPanelStyle = {
    position: "sticky",
    top: 14,
    maxHeight: "calc(100vh - 112px)",
    overflow: "auto",
    background: c.bg,
    border: `1px solid ${c.border}`,
    borderRadius: 12,
    padding: 16,
    boxSizing: "border-box",
  };
  const actionFooterStyle = {
    position: "sticky",
    bottom: -16,
    display: "flex",
    justifyContent: "space-between",
    gap: 10,
    margin: "14px -16px -16px",
    padding: "12px 16px",
    flexWrap: "wrap",
    background: c.bg,
    borderTop: `1px solid ${c.border}`,
    borderRadius: "0 0 12px 12px",
  };

  const copyToClipboard = async (text) => {
    try {
      await copyRichTextToClipboard(text);
    } catch {
      // Clipboard may be blocked in some contexts.
    }
  };

  const updateSelectionFrom = (source) => {
    const selected = window.getSelection();
    if (!selected) return;
    const text = selected.toString().trim();
    if (!text) {
      setSelectedPreviewText("");
      setSelectedSource("");
      return;
    }

    const container = source === "befund" ? befundPreviewRef.current : beurteilungPreviewRef.current;
    if (!container) return;
    const anchorNode = selected.anchorNode;
    const focusNode = selected.focusNode;
    const isInside =
      (anchorNode && container.contains(anchorNode)) ||
      (focusNode && container.contains(focusNode));
    if (!isInside) return;

    setSelectedPreviewText(text);
    setSelectedSource(source);
  };

  const applySelectedText = () => {
    if (!previewVorlage || !selectedPreviewText || !selectedSource) return;
    if (selectedSource === "befund") {
      loadVorlageToEditor({ ...previewVorlage, befund: selectedPreviewText, beurteilung: "" }, "befund");
    } else {
      loadVorlageToEditor({ ...previewVorlage, befund: "", beurteilung: selectedPreviewText }, "beurteilung");
    }
    closePreview();
  };

  const confirmCreateSectionModal = async () => {
    const ok = await Promise.resolve(onCreateVorlagenGroup?.());
    if (ok) {
      setIsCreateSectionModalOpen(false);
    }
  };

  useEffect(() => {
    if (!previewVorlage) return;

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        requestClosePreview();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [previewVorlage, previewDraft, requestClosePreview]);

  useEffect(() => {
    if (!isCreateSectionModalOpen) return;

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        setIsCreateSectionModalOpen(false);
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isCreateSectionModalOpen]);

  return (
    <div style={{ padding: "18px 22px" }}>
      <div style={{ marginBottom: 16 }}>
        <input type="text" value={vorlagenFilterText} onChange={(e) => setVorlagenFilterText(e.target.value)} placeholder="Vorlagen durchsuchen..." style={{ ...inp, width: 260 }} />
      </div>

      <div style={{ padding: 14, background: c.bgSecondary, borderRadius: "12px", border: `1px solid ${c.border}`, marginBottom: 20 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer" }} onClick={() => setNewPromptFormOpen(!newPromptFormOpen)}>
          <span style={lbl}>Neue Vorlage direkt erstellen</span>
          <span style={{ fontSize: 16 }}>{newPromptFormOpen ? "▾" : "▸"}</span>
        </div>
        {newPromptFormOpen && (
          <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 10 }}>
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <div style={{ display: "flex", gap: 6 }}>
              {creationGroupOptions.map((m) => {
                const isActive = newPromptMod === m;
                const mc = modColor(m);
                return <button key={m} onClick={() => setNewPromptMod(m)} style={{ padding: "6px 16px", fontSize: 12, fontWeight: 600, borderRadius: "8px", cursor: "pointer", border: `1px solid ${c.border}`, background: isActive ? mc.bg : c.bg, color: isActive ? mc.color : c.textSecondary }}>{m}</button>;
              })}
              <button
                type="button"
                onClick={() => setIsCreateSectionModalOpen(true)}
                style={{
                  padding: "6px 12px",
                  fontSize: 12,
                  fontWeight: 600,
                  borderRadius: "8px",
                  cursor: "pointer",
                  border: `1px dashed ${c.border}`,
                  background: c.bg,
                  color: c.textSecondary,
                }}
              >
                + Modalität/Sektion anlegen (z. B. PET)
              </button>
            </div>
          </div>
          {newPromptMod === "MRT" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: ".04em", textTransform: "uppercase", color: c.textSecondary }}>
                Speichern unter
              </span>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {["MRT", "MRT 1,5", "MRT 3"].map((target) => {
                  const isActive = newPromptMrtTarget === target;
                  const label = target === "MRT" ? "MRT gemeinsam" : target === "MRT 1,5" ? "MRT 1,5T" : "MRT 3T";
                  const mc = modColor(target);
                  return (
                    <button
                      key={target}
                      onClick={() => setNewPromptMrtTarget(target)}
                      style={{
                        padding: "6px 12px",
                        fontSize: 12,
                        fontWeight: 600,
                        borderRadius: "8px",
                        cursor: "pointer",
                        border: `1px solid ${c.border}`,
                        background: isActive ? mc.bg : c.bg,
                        color: isActive ? mc.color : c.textSecondary,
                      }}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          {canCreateSubsection(newPromptMod === "MRT" ? newPromptMrtTarget : newPromptMod) && (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <label style={{ ...lbl }}>Untersektion</label>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <select 
                  value={newPromptSubsection} 
                  onChange={(e) => setNewPromptSubsection(e.target.value)} 
                  style={{ ...inp, flex: 1 }}
                >
                  <option value="">-- Neue Untersektion oder wählen --</option>
                  {(() => {
                    const modGroup = newPromptMod === "MRT" ? newPromptMrtTarget : newPromptMod;
                    const vorlagen = vorlagenGruppiert[modGroup] || [];
                    const subsections = new Set();
                    vorlagen.forEach((v) => {
                      const sub = getVorlageSubsection(v);
                      if (sub) subsections.add(sub.trim());
                    });
                    return Array.from(subsections).sort((a, b) => a.localeCompare(b, "de", { sensitivity: "base" })).map((sub) => (
                      <option key={sub} value={sub}>{sub}</option>
                    ));
                  })()}
                </select>
              </div>
              <input
                type="text"
                value={newPromptSubsection}
                onChange={(e) => setNewPromptSubsection(e.target.value)}
                placeholder="Oder neue Untersektion eingeben (z. B. Kopf)..."
                style={{ ...inp }}
              />
            </div>
          )}
          {canCreateSubsection(newPromptMod === "MRT" ? newPromptMrtTarget : newPromptMod) && (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <label style={{ ...lbl }}>Kategorie</label>
              <input
                type="text"
                value={newPromptCategory}
                onChange={(e) => setNewPromptCategory(e.target.value)}
                placeholder="z. B. Kardiomyopathien, Klappen / Vitien, Kurzbausteine..."
                list="vorlagen-category-options"
                style={{ ...inp }}
              />
              <datalist id="vorlagen-category-options">
                {categoryOptions.map((category) => (
                  <option key={category} value={category} />
                ))}
              </datalist>
            </div>
          )}
          <input type="text" value={newPromptName} onChange={(e) => setNewPromptName(e.target.value)} placeholder={newPromptMod === "Modalitäten" ? "Name der Modalität oder Kategorie..." : newPromptSubsection.trim() ? "Vorlagenname (z. B. Kopf nativ)..." : "Vorlagenname (= Körperregion)..."} style={{ ...inp }} />
          <RichTextEditor value={newPromptBefund} onChange={setNewPromptBefund} placeholder="Befund / Inhalt..." minHeight={400} c={c} dark={dark} ariaLabel="Vorlage Befund" />
          <RichTextEditor value={newPromptBeurteilung} onChange={setNewPromptBeurteilung} placeholder="Beurteilung Vorlage (optional)..." minHeight={120} c={c} dark={dark} ariaLabel="Vorlage Beurteilung" />
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <button type="button" onClick={() => { setNewPromptSubsection(""); setNewPromptCategory(""); setNewPromptName(""); setNewPromptBefund(""); setNewPromptBeurteilung(""); }} style={{ background: dark ? "#2d1515" : "#fff0f0", color: dark ? "#fc8181" : "#cc3333", border: `1px solid ${dark ? "#4a2020" : "#ffcccc"}`, borderRadius: "8px", cursor: "pointer", padding: "6px 12px", fontSize: 12 }}>Abbrechen</button>
            <button type="button" onClick={() => {
              if (!newPromptName.trim()) {
                showStatus("Bitte Name eingeben.", true);
                return;
              }
              if (!newPromptBefund.trim()) {
                showStatus("Inhalt darf nicht leer sein.", true);
                return;
              }
              onSaveNewPrompt();
            }} disabled={isSavingNewPrompt} style={{ background: dark ? "#0d2d1a" : "#f0fff4", color: dark ? "#48bb78" : "#276749", border: `1px solid ${dark ? "#48bb78" : "#9ae6b4"}`, borderRadius: "8px", cursor: isSavingNewPrompt ? "not-allowed" : "pointer", padding: "6px 12px", fontSize: 12, display: "inline-flex", alignItems: "center", gap: 5, opacity: isSavingNewPrompt ? 0.6 : 1 }}>{isSavingNewPrompt ? "⏳ Speichern..." : "💾 Speichern"}</button>
          </div>
        </div>
        )}
      </div>

      <div style={workspaceStyle}>
        <div style={{ minWidth: 0 }}>
      {(vorlagenGroupOptions || []).map((modGroup, groupIndex, groupList) => {
        const vorlagen = vorlagenGruppiert[modGroup] || [];
        const groupKey = `prompt-group:${modGroup}`;
        const groupedSubsections = groupedVorlagenByMod[modGroup] || [];
        if (isSearching && groupedSubsections.length === 0) return null;

        const isGroupOpen = isSearching || !!expandedVorlagenGroups[groupKey];
        return (
          <div key={modGroup} style={{ marginBottom: 28 }}>
            <div
              onClick={() => toggleVorlagenGroup(groupKey)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                marginBottom: isGroupOpen ? 10 : 0,
                padding: "10px 12px",
                border: `1px solid ${c.border}`,
                borderRadius: "10px",
                background: c.bgSecondary,
                cursor: "pointer",
              }}
            >
              <span style={{ fontSize: 13 }}>{isGroupOpen ? "▾" : "▸"}</span>
              <span style={modBadge(modGroup)}>{modGroup}</span>
              <span style={{ fontSize: 13, fontWeight: 500 }}>{getGroupLabel(modGroup)}</span>
              <span style={{ fontSize: 12, color: c.textSecondary, marginLeft: "auto" }}>{vorlagen.length} Vorlagen</span>
              <IconButton
                c={c}
                dark={dark}
                label="Sektion nach oben"
                onClick={(e) => {
                  e.stopPropagation();
                  onMoveVorlagenGroup?.(modGroup, "up");
                }}
                disabled={groupIndex === 0}
              >
                <UiIcon.ArrowUp />
              </IconButton>
              <IconButton
                c={c}
                dark={dark}
                label="Sektion nach unten"
                onClick={(e) => {
                  e.stopPropagation();
                  onMoveVorlagenGroup?.(modGroup, "down");
                }}
                disabled={groupIndex === groupList.length - 1}
              >
                <UiIcon.ArrowDown />
              </IconButton>
              <IconButton
                c={c}
                dark={dark}
                tone="danger"
                label="Sektion löschen"
                onClick={(e) => {
                  e.stopPropagation();
                  void onDeleteVorlagenGroup?.(modGroup);
                }}
              >
                <UiIcon.Trash />
              </IconButton>
            </div>
            {isGroupOpen && (
              <div style={{ paddingLeft: 14, paddingTop: 2 }}>
                {canCreateSubsection(modGroup) && (
                  <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 10, flexWrap: "wrap" }}>
                    <input
                      type="text"
                      value={newSubsectionDraftByMod[modGroup] || ""}
                      onChange={(e) => setNewSubsectionDraftByMod((prev) => ({ ...prev, [modGroup]: e.target.value }))}
                      placeholder="Neue Untersektion (z. B. Herz, Kopf)..."
                      style={{ ...inp, minWidth: 260, flex: "1 1 260px" }}
                    />
                    <button
                      type="button"
                      onClick={() => createNewSubsection(modGroup)}
                      style={{
                        background: dark ? "#0d2d1a" : "#f0fff4",
                        color: dark ? "#48bb78" : "#276749",
                        border: `1px solid ${dark ? "#48bb78" : "#9ae6b4"}`,
                        borderRadius: "8px",
                        cursor: "pointer",
                        padding: "6px 12px",
                        fontSize: 12,
                        fontWeight: 600,
                      }}
                    >
                      + Untersektion anlegen
                    </button>
                  </div>
                )}
                {groupedSubsections.length === 0 ? (
                  <div style={{ fontSize: 12, color: c.textSecondary, padding: "8px 0" }}>{vorlagenFilterText ? "Keine Treffer." : "Noch leer."}</div>
                ) : groupedSubsections.map(([subsection, list], subsectionIndex) => {
                  const subsectionKey = `subsection:${modGroup}:${subsection}`;
                  const subsectionNames = groupedSubsections.map(([name]) => name);
                  const isSubsectionOpen = isSearching || expandedSubsections[subsectionKey] === true;
                  const isRenaming = editingSubsection.key === subsectionKey;
                  const categoryListKey = getSubsectionCategoryKey(modGroup, subsection);
                  const subsectionCategoryOptions = getSubsectionCategoryOptions(list, categoryListKey);
                  const categoryGroups = subsectionCategoryOptions
                    .map((category) => ({
                      category,
                      items: list.filter((vorlage) => normalizeCategory(getVorlageCategoryValue(categoryById, vorlage)) === category),
                    }))
                    .filter((entry) => !isSearching || entry.items.length > 0);
                  const isCreatingCategory = activeCategoryCreateKey === subsectionKey;

                  return (
                    <div key={subsectionKey} style={{ marginBottom: 12 }}>
                      <div
                        onClick={() => toggleSubsection(subsectionKey)}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          background: c.bgSecondary,
                          border: `1px solid ${c.border}`,
                          borderRadius: "8px",
                          padding: "8px 10px",
                          cursor: "pointer",
                          marginBottom: isSubsectionOpen ? 8 : 0,
                        }}
                      >
                        <span style={{ fontSize: 12 }}>{isSubsectionOpen ? "▾" : "▸"}</span>
                        {isRenaming ? (
                          <input
                            type="text"
                            value={editingSubsection.value}
                            onChange={(e) => setEditingSubsection((prev) => ({ ...prev, value: e.target.value }))}
                            onClick={(e) => e.stopPropagation()}
                            onKeyDown={(e) => {
                              e.stopPropagation();
                              if (e.key === "Enter") {
                                void handleRenameSubsection(modGroup, subsection, list);
                              }
                              if (e.key === "Escape") {
                                cancelRenameSubsection();
                              }
                            }}
                            placeholder="Untersektion"
                            style={{ ...inp, maxWidth: 240, padding: "4px 8px", fontSize: 12 }}
                          />
                        ) : (
                          <span style={{ fontSize: 12, fontWeight: 600 }}>{subsection}</span>
                        )}
                        <span style={{ fontSize: 11, color: c.textSecondary, marginLeft: "auto" }}>{list.length} Vorlage{list.length === 1 ? "" : "n"} · {subsectionCategoryOptions.length} Kategorien</span>
                        {isRenaming ? (
                          <>
                            <IconButton
                              c={c}
                              dark={dark}
                              tone="primary"
                              label="Untersektion speichern"
                              onClick={(e) => {
                                e.stopPropagation();
                                void handleRenameSubsection(modGroup, subsection, list);
                              }}
                            >
                              💾
                            </IconButton>
                            <IconButton
                              c={c}
                              dark={dark}
                              label="Bearbeiten abbrechen"
                              onClick={(e) => {
                                e.stopPropagation();
                                cancelRenameSubsection();
                              }}
                            >
                              ↗
                            </IconButton>
                          </>
                        ) : (
                          <>
                            <IconButton
                              c={c}
                              dark={dark}
                              label="Untersektion nach oben"
                              onClick={(e) => {
                                e.stopPropagation();
                                onMoveSubsection?.(modGroup, subsection, "up", subsectionNames);
                              }}
                              disabled={subsectionIndex === 0}
                            >
                              <UiIcon.ArrowUp />
                            </IconButton>
                            <IconButton
                              c={c}
                              dark={dark}
                              label="Untersektion nach unten"
                              onClick={(e) => {
                                e.stopPropagation();
                                onMoveSubsection?.(modGroup, subsection, "down", subsectionNames);
                              }}
                              disabled={subsectionIndex === groupedSubsections.length - 1}
                            >
                              <UiIcon.ArrowDown />
                            </IconButton>
                            <IconButton
                              c={c}
                              dark={dark}
                              label="Untersektion bearbeiten"
                              onClick={(e) => {
                                e.stopPropagation();
                                beginRenameSubsection(subsectionKey, subsection);
                              }}
                            >
                              <UiIcon.Edit />
                            </IconButton>
                            <IconButton
                              c={c}
                              dark={dark}
                              tone="info"
                              label="Kategorie hinzufügen"
                              onClick={(e) => {
                                e.stopPropagation();
                                setActiveCategoryCreateKey((current) => (current === subsectionKey ? "" : subsectionKey));
                              }}
                            >
                              <UiIcon.Add />
                            </IconButton>
                            <IconButton
                              c={c}
                              dark={dark}
                              tone="danger"
                              label="Untersektion löschen"
                              onClick={(e) => {
                                e.stopPropagation();
                                void handleDeleteSubsection(modGroup, subsection, list);
                              }}
                            >
                              <UiIcon.Trash />
                            </IconButton>
                          </>
                        )}
                        {canCreateSubsection(modGroup) && (
                          <IconButton
                            c={c}
                            dark={dark}
                            tone="info"
                            label="Vorlage hinzufügen"
                            onClick={(e) => {
                              e.stopPropagation();
                              applySubsectionToForm(modGroup, subsection);
                            }}
                          >
                            <UiIcon.Add />
                          </IconButton>
                        )}
                      </div>
                      {isSubsectionOpen && list.length === 0 && (
                        <div style={{ padding: "8px 0 10px 14px" }}>
                          <AppButton c={c} dark={dark} tone="info" onClick={() => applySubsectionToForm(modGroup, subsection)}>
                            + Vorlage in dieser Untersektion erstellen
                          </AppButton>
                        </div>
                      )}
                      {isSubsectionOpen && isCreatingCategory && (
                        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", padding: "0 0 8px 14px" }}>
                          <input
                            type="text"
                            value={newCategoryDraftBySubsection[`${subsectionKey}:draft`] || ""}
                            onChange={(e) => setNewCategoryDraftBySubsection((prev) => ({ ...prev, [`${subsectionKey}:draft`]: e.target.value }))}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") addCategoryToSubsection(modGroup, subsection);
                              if (e.key === "Escape") setActiveCategoryCreateKey("");
                            }}
                            placeholder="Neue Kategorie..."
                            list="vorlagen-category-options"
                            style={{ ...inp, minWidth: 240, flex: "1 1 240px", padding: "5px 8px", fontSize: 12 }}
                            autoFocus
                          />
                          <AppButton c={c} dark={dark} tone="primary" compact onClick={() => addCategoryToSubsection(modGroup, subsection)}>
                            Kategorie erstellen
                          </AppButton>
                          <AppButton c={c} dark={dark} compact onClick={() => setActiveCategoryCreateKey("")}>
                            Abbrechen
                          </AppButton>
                        </div>
                      )}
                      {isSubsectionOpen && categoryGroups.map(({ category, items }) => {
                        const categoryKey = `${categoryListKey}:category:${category}`;
                        const isCategoryOpen = isSearching || !!expandedSubsections[categoryKey];

                        return (
                          <div key={category} style={{ margin: "0 0 8px 14px" }}>
                            <div onClick={() => toggleSubsection(categoryKey)} style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 10px", background: c.bgSecondary, border: `1px solid ${c.border}`, borderRadius: 7, cursor: "pointer", marginBottom: isCategoryOpen ? 6 : 0 }}>
                              <span style={{ fontSize: 12 }}>{isCategoryOpen ? "▾" : "▸"}</span>
                              <span style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".04em", color: c.textSecondary }}>{category}</span>
                              <span style={{ fontSize: 11, color: c.textSecondary, marginLeft: "auto" }}>{items.length}</span>
                            </div>
                            {isCategoryOpen && items.map((vorlage) => (
                              <div
                                key={vorlage.id}
                                onClick={() => openPreview(vorlage)}
                                aria-current={previewVorlage?.id === vorlage.id ? "true" : undefined}
                                style={{
                                  background: previewVorlage?.id === vorlage.id ? c.bgSecondary : c.bg,
                                  border: `1px solid ${previewVorlage?.id === vorlage.id ? (dark ? "#63b3ed" : "#2b6cb0") : c.border}`,
                                  borderRadius: "8px",
                                  padding: "10px 12px",
                                  cursor: "pointer",
                                  marginBottom: 6,
                                  boxShadow: previewVorlage?.id === vorlage.id ? (dark ? "inset 3px 0 0 #63b3ed" : "inset 3px 0 0 #2b6cb0") : "none",
                                }}
                              >
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                                  <div style={{ flex: 1 }}>
                                    <div style={{ fontWeight: 500, fontSize: 13, marginBottom: 4 }}>{getVorlageDisplayName(vorlage)}</div>
                                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
                                      <span style={{ ...modBadge(vorlage.mod), fontSize: 10 }}>{vorlage.mod}</span>
                                      <span style={{ fontSize: 11, color: c.textSecondary, background: c.bgSecondary, padding: "1px 7px", borderRadius: 99 }}>{getVorlageDisplayName(vorlage)}</span>
                                    </div>
                                  </div>
                                  <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                                    <select
                                      value={normalizeCategory(getVorlageCategoryValue(categoryById, vorlage))}
                                      onClick={(e) => e.stopPropagation()}
                                      onChange={(e) => {
                                        e.stopPropagation();
                                        void changeVorlageCategory(vorlage, e.target.value);
                                      }}
                                      title="Kategorie wechseln"
                                      style={{ ...inp, width: 180, padding: "4px 8px", fontSize: 11, background: c.bg }}
                                    >
                                      {subsectionCategoryOptions.map((option) => (
                                        <option key={option} value={option}>{option}</option>
                                      ))}
                                    </select>
                                    <AppButton
                                      c={c}
                                      dark={dark}
                                      tone="primary"
                                      compact
                                      title="Vorlage direkt übernehmen"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        loadVorlageToEditor(vorlage, "all");
                                      }}
                                      disabled={!hasRichTextContent(vorlage.befund) && !hasRichTextContent(vorlage.beurteilung)}
                                    >
                                      Übernehmen
                                    </AppButton>
                                    <ActionMenu c={c} dark={dark} label={`Aktionen für ${getVorlageDisplayName(vorlage)}`}>
                                      <IconButton c={c} dark={dark} tone="danger" label="Vorlage löschen" onClick={() => onDeletePrompt(vorlage.id)}>
                                        <UiIcon.Trash />
                                      </IconButton>
                                    </ActionMenu>
                                  </div>
                              </div>
                                <div style={{ fontSize: 11, color: c.textSecondary, marginTop: 6, fontFamily: "monospace", lineHeight: 1.6, whiteSpace: "pre-wrap", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{richTextToPlainText(vorlage.befund)}</div>
                                {hasRichTextContent(vorlage.beurteilung) && <div style={{ fontSize: 11, color: c.textTertiary, marginTop: 4, fontStyle: "italic", whiteSpace: "pre-wrap", display: "-webkit-box", WebkitLineClamp: 1, WebkitBoxOrient: "vertical", overflow: "hidden" }}>↳ {richTextToPlainText(vorlage.beurteilung)}</div>}
                              </div>
                            ))}
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
        </div>

      {previewVorlage && (
        <aside style={splitPanelStyle} aria-label="Vorlage bearbeiten">
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
              <div style={{ flex: "1 1 auto", minWidth: 0, maxWidth: 760 }}>
                {currentPreview?.mod !== "Modalitäten" && (
                  <input
                    type="text"
                    value={previewDraft?.subsection || ""}
                    onChange={(e) => setPreviewDraft((prev) => ({ ...prev, subsection: e.target.value }))}
                    placeholder="Untersektion"
                    style={{ ...inp, width: "100%", marginBottom: 8, fontSize: 12 }}
                  />
                )}
                <input
                  type="text"
                  value={previewDraft?.name || ""}
                  onChange={(e) => setPreviewDraft((prev) => ({ ...prev, name: e.target.value }))}
                  style={{ ...inp, width: "100%", fontSize: 14, fontWeight: 600 }}
                />
                <input
                  type="text"
                  value={previewDraft?.category || ""}
                  onChange={(e) => setPreviewDraft((prev) => ({ ...prev, category: e.target.value }))}
                  placeholder="Kategorie"
                  list="vorlagen-category-options"
                  style={{ ...inp, width: "100%", marginTop: 8, fontSize: 12 }}
                />
                <div style={{ marginTop: 4, display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                  <span style={{ ...modBadge(currentPreview?.mod), fontSize: 10 }}>{currentPreview?.mod}</span>
                  <span style={{ fontSize: 11, color: c.textSecondary }}>{normalizeCategory(previewDraft?.category || getVorlageCategoryValue(categoryById, currentPreview))}</span>
                </div>
              </div>
              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                {previewIsDirty && <span style={{ fontSize: 11, color: dark ? "#f6ad55" : "#b45309", fontWeight: 700 }}>Nicht gespeichert</span>}
                <IconButton c={c} dark={dark} tone={previewIsDirty ? "primary" : "neutral"} onClick={saveInlineEdit} disabled={isSavingPreview || !previewIsDirty} label="Speichern">💾</IconButton>
                <IconButton c={c} dark={dark} onClick={requestClosePreview} label="Editor schließen">↗</IconButton>
              </div>
            </div>

            <div style={{ display: "grid", gap: 12 }}>
              <TextPanel c={c} label="Befund">
                <div
                  ref={befundPreviewRef}
                  onMouseUp={() => updateSelectionFrom("befund")}
                  style={{ userSelect: "text" }}
                >
                  <RichTextEditor
                    value={previewDraft?.befund || ""}
                    onChange={(value) => setPreviewDraft((prev) => ({ ...prev, befund: value }))}
                    minHeight="52vh"
                    c={c}
                    dark={dark}
                    ariaLabel="Vorlage Befund bearbeiten"
                  />
                </div>
              </TextPanel>

              <TextPanel c={c} label="Beurteilung">
                <div
                  ref={beurteilungPreviewRef}
                  onMouseUp={() => updateSelectionFrom("beurteilung")}
                  style={{ userSelect: "text" }}
                >
                  <RichTextEditor
                    value={previewDraft?.beurteilung || ""}
                    onChange={(value) => setPreviewDraft((prev) => ({ ...prev, beurteilung: value }))}
                    minHeight="22vh"
                    c={c}
                    dark={dark}
                    ariaLabel="Vorlage Beurteilung bearbeiten"
                  />
                </div>
              </TextPanel>
            </div>

            <div style={actionFooterStyle}>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <AppButton c={c} dark={dark} tone="warning" onClick={() => copyToClipboard(currentPreview?.befund || "")} disabled={!hasRichTextContent(currentPreview?.befund)}>Befund kopieren</AppButton>
                <AppButton c={c} dark={dark} tone="warning" onClick={() => copyToClipboard(currentPreview?.beurteilung || "")} disabled={!hasRichTextContent(currentPreview?.beurteilung)}>Beurteilung kopieren</AppButton>
              </div>

              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
                <AppButton c={c} dark={dark} onClick={applySelectedText} disabled={!selectedPreviewText}>Auswahl übernehmen</AppButton>
                <AppButton c={c} dark={dark} tone="info" onClick={() => { loadVorlageToEditor(currentPreview, "befund"); closePreview(); }} disabled={!hasRichTextContent(currentPreview?.befund)}>Nur Befund übernehmen</AppButton>
                <AppButton c={c} dark={dark} tone="info" onClick={() => { loadVorlageToEditor(currentPreview, "beurteilung"); closePreview(); }} disabled={!hasRichTextContent(currentPreview?.beurteilung)}>Nur Beurteilung übernehmen</AppButton>
                <AppButton c={c} dark={dark} tone="primary" onClick={() => { loadVorlageToEditor(currentPreview, "all"); closePreview(); }}>Übernehmen</AppButton>
                <AppButton c={c} dark={dark} tone="danger" onClick={requestClosePreview}>Schließen</AppButton>
              </div>
            </div>
        </aside>
      )}
      </div>

      {isCreateSectionModalOpen && (
        <div
          onClick={() => setIsCreateSectionModalOpen(false)}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            padding: 16,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "min(520px, 95vw)",
              background: c.bg,
              border: `1px solid ${c.border}`,
              borderRadius: 12,
              padding: 16,
            }}
          >
            <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 10 }}>Neue Modalität/Sektion anlegen</div>
            <input
              type="text"
              value={newVorlagenGroupName}
              onChange={(e) => setNewVorlagenGroupName(e.target.value)}
              placeholder="Modalität/Sektion eingeben (z. B. PET)..."
              style={{ ...inp, width: "100%" }}
              autoFocus
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  void confirmCreateSectionModal();
                }
              }}
            />
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12 }}>
              <button
                type="button"
                onClick={() => setIsCreateSectionModalOpen(false)}
                style={{
                  background: dark ? "#2d1515" : "#fff0f0",
                  color: dark ? "#fc8181" : "#cc3333",
                  border: `1px solid ${dark ? "#4a2020" : "#ffcccc"}`,
                  borderRadius: "8px",
                  cursor: "pointer",
                  padding: "6px 12px",
                  fontSize: 12,
                }}
              >
                Abbrechen
              </button>
              <button
                type="button"
                onClick={() => void confirmCreateSectionModal()}
                style={{
                  background: dark ? "#0d2d1a" : "#f0fff4",
                  color: dark ? "#48bb78" : "#276749",
                  border: `1px solid ${dark ? "#48bb78" : "#9ae6b4"}`,
                  borderRadius: "8px",
                  cursor: "pointer",
                  padding: "6px 12px",
                  fontSize: 12,
                  fontWeight: 600,
                }}
              >
                Erstellen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default memo(VorlagenTab);
